<template>
  <v-container fluid class="waste-home pa-3 pa-md-4">
    <div class="d-flex flex-wrap align-center ga-2 mb-3">
      <h1 class="text-h6 text-md-h5">{{ $t("waste.list.title") }}</h1>
      <v-chip
        v-if="list.provisional"
        size="small"
        color="amber-darken-2"
        variant="tonal"
      >
        {{ $t("waste.provisional_chip") }}
      </v-chip>
      <v-spacer />
      <v-btn
        v-if="list.canCreate"
        color="red-darken-3"
        prepend-icon="mdi-plus"
        @click="dialog = true"
      >
        {{ $t("waste.list.new") }}
      </v-btn>
    </div>

    <v-text-field
      v-model="search"
      :label="$t('waste.list.search_label')"
      prepend-inner-icon="mdi-magnify"
      variant="outlined"
      density="comfortable"
      clearable
      hide-details
      :maxlength="queryMax"
      class="mb-3"
    />

    <!-- The chip row scrolls sideways on its own; the page never does. -->
    <div class="waste-filters mb-3">
      <v-chip
        v-for="chip in chips"
        :key="chip.view"
        :color="chip.color"
        :variant="chip.view === view ? 'flat' : 'outlined'"
        class="mr-2"
        @click="selectView(chip.view)"
      >
        {{ $t(chip.key) }} · {{ chip.count }}
      </v-chip>
      <v-menu>
        <template #activator="{ props }">
          <v-chip
            v-bind="props"
            :variant="code ? 'flat' : 'outlined'"
            color="primary"
            append-icon="mdi-menu-down"
            class="mr-2"
          >
            {{ code || $t("waste.list.filter_code") }}
          </v-chip>
        </template>
        <v-list density="compact" max-height="360">
          <v-list-item @click="selectCode('')">
            <v-list-item-title>{{
              $t("waste.list.all_codes")
            }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-for="item in codeChoices"
            :key="item.codeKey"
            @click="selectCode(item.code)"
          >
            <v-list-item-title>{{ item.code }}</v-list-item-title>
            <v-list-item-subtitle class="text-wrap">
              {{ item.description }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div v-if="loading">
      <v-skeleton-loader
        v-for="n in 3"
        :key="n"
        type="list-item-two-line"
        class="mb-2"
      />
    </div>

    <v-alert v-else-if="error" type="error" variant="tonal">
      <div class="d-flex flex-wrap align-center ga-2">
        <span class="flex-grow-1">{{ $t("waste.load_error") }}</span>
        <v-btn variant="outlined" size="small" @click="load">
          {{ $t("waste.retry") }}
        </v-btn>
      </div>
    </v-alert>

    <div
      v-else-if="!groups.length"
      class="text-body-1 text-medium-emphasis text-center py-8"
    >
      {{ $t(emptyText) }}
    </div>

    <template v-else>
      <v-card
        v-for="group in groups"
        :key="group.codeKey"
        variant="outlined"
        class="mb-4"
      >
        <v-card-title class="text-wrap pb-0">
          <span class="font-weight-bold mr-2">{{ group.code }}</span>
          <span class="text-body-1">{{ group.description }}</span>
          <v-chip
            v-if="group.hazardous"
            size="x-small"
            color="red"
            variant="tonal"
            class="ml-2"
          >
            {{ $t("waste.hazardous") }}
          </v-chip>
        </v-card-title>
        <v-card-subtitle class="text-wrap waste-totals">
          <span v-for="(part, i) in group.totals" :key="i">{{
            (i ? " · " : "") + $t(part.key, part.params)
          }}</span>
        </v-card-subtitle>

        <!-- Phone: one card per container. -->
        <div class="d-md-none pa-2">
          <v-card
            v-for="row in group.rows"
            :key="row.id"
            :to="containerRoute(row)"
            variant="tonal"
            class="mb-2"
            :class="{ 'text-medium-emphasis': row.inactive }"
          >
            <v-card-text>
              <div class="text-subtitle-1 font-weight-bold">
                {{ row.name }}
                <v-chip v-if="row.inactive" size="x-small" class="ml-1">
                  {{ $t("waste.status.inactive") }}
                </v-chip>
              </div>
              <div class="text-body-2 text-medium-emphasis">
                {{ $t(row.typeKey) }} · {{ $t(row.unitKey) }}
              </div>
              <div class="text-body-2 my-1">
                {{ $t(row.recorded.key, row.recorded.params) }}
              </div>
              <div class="d-flex flex-wrap ga-2">
                <v-chip
                  size="small"
                  variant="tonal"
                  :color="row.weighing.color"
                >
                  {{ $t(row.weighing.key, row.weighing.params) }}
                </v-chip>
                <v-chip v-if="row.sendOut" size="small" variant="tonal">
                  {{ $t(row.sendOut.key, row.sendOut.params) }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Desktop: a table that wraps long names instead of scrolling sideways. -->
        <v-table class="d-none d-md-block waste-table" density="comfortable">
          <thead>
            <tr>
              <th>{{ $t("waste.list.columns.container") }}</th>
              <th>{{ $t("waste.list.columns.type") }}</th>
              <th>{{ $t("waste.list.columns.content") }}</th>
              <th>{{ $t("waste.list.columns.weight") }}</th>
              <th>{{ $t("waste.list.columns.last_send_out") }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in group.rows"
              :key="row.id"
              :class="{ 'text-medium-emphasis': row.inactive }"
            >
              <td class="font-weight-bold">
                {{ row.name }}
                <v-chip v-if="row.inactive" size="x-small" class="ml-1">
                  {{ $t("waste.status.inactive") }}
                </v-chip>
              </td>
              <td>{{ $t(row.typeKey) }} · {{ $t(row.unitKey) }}</td>
              <td>{{ $t(row.recorded.key, row.recorded.params) }}</td>
              <td>
                <v-chip
                  size="small"
                  variant="tonal"
                  :color="row.weighing.color"
                >
                  {{ $t(row.weighing.key, row.weighing.params) }}
                </v-chip>
              </td>
              <td>
                {{
                  row.sendOut ? $t(row.sendOut.key, row.sendOut.params) : "—"
                }}
              </td>
              <td class="text-right text-no-wrap">
                <v-btn
                  size="small"
                  variant="outlined"
                  color="primary"
                  class="mr-1"
                  :to="containerRoute(row)"
                >
                  {{ $t("waste.list.open") }}
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  prepend-icon="mdi-qrcode"
                  @click="printLabel(group, row)"
                >
                  {{ $t("waste.list.label") }}
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card>

      <div class="text-caption text-medium-emphasis mt-2">
        {{ $t("waste.list.litres_note") }}
      </div>
    </template>

    <container-dialog v-model="dialog" @saved="onCreated" />

    <v-snackbar v-model="printError" color="error" location="top">
      {{ $t("waste.print_error") }}
    </v-snackbar>
  </v-container>
</template>

<script>
// Waste containers "Rifiuti e contenitori" (Stage 6 plan §D): containers grouped by waste code,
// with filters, search, phone cards and a desktop table. The route needs the waste switch (router
// meta requiresWaste); the API checks it again. "Nuovo contenitore" shows for a Manager / Admin
// (canCreate from the API, which checks the role again).
import { listWasteContainers } from "@/api/compliance";
import ContainerDialog from "@/components/Waste/containerDialog.vue";
import printContainerLabels from "@/components/Waste/print";
import {
  DEFAULT_VIEW,
  QUERY_MAX,
  cleanQuery,
  emptyKey,
  filterChips,
  groupView,
} from "@/components/Waste/wasteList.mjs";

const SEARCH_DELAY_MS = 300;

export default {
  components: { ContainerDialog },
  data: () => ({
    view: DEFAULT_VIEW,
    code: "",
    search: "",
    query: "",
    loading: true,
    error: false,
    list: { groups: [], counts: {}, provisional: false, canCreate: false },
    // The code menu lists the codes seen without a code filter.
    codeChoices: [],
    requestSeq: 0,
    searchTimer: null,
    dialog: false,
    printError: false,
    queryMax: QUERY_MAX,
  }),
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    chips() {
      return filterChips(this.list.counts);
    },
    groups() {
      return (this.list.groups || []).map((g) => groupView(g, this.locale));
    },
    emptyText() {
      return emptyKey(this.view, { query: this.query, code: this.code });
    },
  },
  watch: {
    search(value) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        const query = cleanQuery(value);
        if (query === this.query) return;
        this.query = query;
        this.load();
      }, SEARCH_DELAY_MS);
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    // Only the latest request may fill the list.
    async load() {
      this.requestSeq += 1;
      const seq = this.requestSeq;
      this.loading = true;
      this.error = false;
      try {
        const list = await listWasteContainers({
          view: this.view,
          code: this.code,
          q: this.query,
        });
        if (seq !== this.requestSeq) return;
        this.list = list;
        if (!this.code) {
          this.codeChoices = (list.groups || []).map((g) => ({
            code: g.code,
            codeKey: g.codeKey,
            description: g.description,
          }));
        }
      } catch (err) {
        if (seq !== this.requestSeq) return;
        this.error = true;
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    selectView(view) {
      if (view === this.view) return;
      this.view = view;
      this.load();
    },
    selectCode(code) {
      if (code === this.code) return;
      this.code = code;
      this.load();
    },
    containerRoute(row) {
      return { name: "waste-container", params: { containerId: row.id } };
    },
    async printLabel(group, row) {
      try {
        await printContainerLabels([
          { id: row.id, name: row.name, waste: { code: group.code } },
        ]);
      } catch (err) {
        this.printError = true;
      }
    },
    async onCreated(detail, { print }) {
      if (print) {
        try {
          await printContainerLabels([detail.container]);
        } catch (err) {
          this.printError = true;
        }
      }
      this.$router.push({
        name: "waste-container",
        params: { containerId: detail.container.id },
      });
    },
  },
};
</script>

<style scoped>
.waste-home {
  max-width: 100%;
  overflow-x: hidden;
}

.waste-filters {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  max-width: 100%;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
}

.waste-filters .v-chip {
  flex: 0 0 auto;
}

.waste-totals {
  opacity: 1;
}

.waste-table :deep(td),
.waste-table :deep(th) {
  white-space: normal;
  word-break: break-word;
}
</style>
